import { Suspense, lazy, useEffect, type ComponentType } from 'react'
import { BrowserRouter, Navigate, Route, Routes } from 'react-router'
import { Layout } from './components/Layout'
import { Loading } from './components/ui'
import { AuthProvider, useAuth } from './lib/auth'
import { APP_NAME, isConfigured } from './lib/supabase'
import { LoginPage } from './pages/Login'
import { MfaPage } from './pages/Mfa'

// Code-split pages (the map library is only loaded where needed).
const page = <T extends Record<string, unknown>>(load: () => Promise<T>, name: keyof T) =>
  lazy(() => load().then((m) => ({ default: m[name] as ComponentType })))
const DashboardPage = page(() => import('./pages/Dashboard'), 'DashboardPage')
const RidesPage = page(() => import('./pages/Rides'), 'RidesPage')
const RideDetailPage = page(() => import('./pages/RideDetail'), 'RideDetailPage')
const DriversPage = page(() => import('./pages/Drivers'), 'DriversPage')
const DriverDetailPage = page(() => import('./pages/DriverDetail'), 'DriverDetailPage')
const PassengersPage = page(() => import('./pages/Passengers'), 'PassengersPage')
const FinancePage = page(() => import('./pages/Finance'), 'FinancePage')
const SupportPage = page(() => import('./pages/Support'), 'SupportPage')
const ReportsPage = page(() => import('./pages/Reports'), 'ReportsPage')
const FaresPage = page(() => import('./pages/Fares'), 'FaresPage')
const SettingsPage = page(() => import('./pages/Settings'), 'SettingsPage')
const AuditPage = page(() => import('./pages/Audit'), 'AuditPage')
const AreasPage = page(() => import('./pages/Areas'), 'AreasPage')
const PlacesPage = page(() => import('./pages/Places'), 'PlacesPage')
const DownloadAppPage = page(() => import('./pages/DownloadApp'), 'DownloadAppPage')

function Guarded() {
  const { loading, session, isStaff, isAdmin, aal, error, signOut } = useAuth()
  if (loading) return <div className="center-page"><Loading label="Verificando sesión…" /></div>
  if (!session) return <LoginPage />
  if (!isStaff) {
    return (
      <div className="center-page">
        <div className="login-card">
          <h1>Acceso restringido</h1>
          <p>{error ?? 'Su cuenta no tiene permisos de administración o soporte.'}</p>
          <button className="btn btn-primary" onClick={() => void signOut()}>Cerrar sesión</button>
        </div>
      </div>
    )
  }
  if (aal !== 'aal2') return <MfaPage />
  return (
    <Suspense fallback={<div className="content"><Loading /></div>}>
      <Routes>
        <Route element={<Layout />}>
          <Route index element={<DashboardPage />} />
          <Route path="carreras" element={<RidesPage />} />
          <Route path="carreras/:id" element={<RideDetailPage />} />
          <Route path="conductores" element={<DriversPage />} />
          <Route path="conductores/:id" element={<DriverDetailPage />} />
          <Route path="pasajeros" element={<PassengersPage />} />
          <Route path="finanzas" element={<FinancePage />} />
          <Route path="soporte" element={<SupportPage />} />
          <Route path="informes" element={<ReportsPage />} />
          <Route path="lugares" element={<PlacesPage />} />
          <Route path="descargar-app" element={<DownloadAppPage />} />
          {isAdmin ? <Route path="tarifas" element={<FaresPage />} /> : null}
          {isAdmin ? <Route path="areas" element={<AreasPage />} /> : null}
          {isAdmin ? <Route path="configuracion" element={<SettingsPage />} /> : null}
          {isAdmin ? <Route path="auditoria" element={<AuditPage />} /> : null}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
    </Suspense>
  )
}

export function App() {
  useEffect(() => {
    document.title = `${APP_NAME} · Administración`
  }, [])
  if (!isConfigured) {
    return (
      <div className="center-page">
        <div className="login-card">
          <h1>Configuración pendiente</h1>
          <p>
            Defina <code>VITE_SUPABASE_URL</code> y <code>VITE_SUPABASE_ANON_KEY</code> en <code>apps/admin/.env</code> (vea{' '}
            <code>.env.example</code> y <code>DEPLOYMENT.md</code>).
          </p>
        </div>
      </div>
    )
  }
  return (
    <AuthProvider>
      <BrowserRouter>
        <Guarded />
      </BrowserRouter>
    </AuthProvider>
  )
}
